import { useTranslation } from "react-i18next";
import { AlertTriangle, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

/**
 * Explains why a session cannot stay alive on this device (device clock far off
 * the real time, or a token refresh loop) instead of silently returning the user
 * to the login page.
 */
export default function SessionHealthAlert() {
  const { t } = useTranslation();
  const { sessionIssue, dismissSessionIssue } = useAuth();
  if (!sessionIssue) return null;

  const minutes = Math.round(Math.abs(sessionIssue.skewSeconds ?? 0) / 60);

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4" role="alert">
      <div className="mx-auto flex max-w-2xl items-start gap-3 rounded-lg border border-destructive/40 bg-card p-4 shadow-lg">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-semibold">{t("auth.sessionHealth.title")}</p>
          <p className="text-sm text-muted-foreground">
            {sessionIssue.kind === "clock_skew" && minutes > 0
              ? t("auth.sessionHealth.clockSkew", { minutes })
              : t("auth.sessionHealth.generic")}
          </p>
          <ul className="list-inside list-disc text-sm text-muted-foreground">
            <li>{t("auth.sessionHealth.stepClock")}</li>
            <li>{t("auth.sessionHealth.stepBrowser")}</li>
          </ul>
        </div>
        <Button variant="ghost" size="icon" onClick={dismissSessionIssue} aria-label={t("common.close")}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
